import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Ship, Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { itineraryApi } from "@/lib/api";
import type { CruiseImportOutcome } from "@/lib/types";

/**
 * "Bring in the cruise days" — the affordance that makes the cruise importer
 * reachable.
 *
 * WHY THIS COMPONENT EXISTS AT ALL. The sailing's day-by-day was already
 * knowable: the cruise line publishes it, and the traveller pasted the link at
 * trip creation. It never reached the itinerary because the research path made
 * an in-memory proposal and nothing ever wrote it down. A backend that can
 * import a sailing but has no button is the same bug one layer up, so the
 * button ships with the importer.
 *
 * WHAT IT PROMISES. Only what the cruise line published. When the page cannot
 * be read, or its day-by-day and its own summary disagree about a port, the
 * import saves NOTHING and this card shows the reason verbatim. It never fills
 * the gap with a plausible Alaska port — an unconfirmed itinerary says it is
 * unconfirmed.
 */
export default function CruiseImportCard({
  tripId,
  /**
   * Do we already hold the sailing's URL? When false the traveller is asked for
   * it, because the alternative is a button that can only fail — and guessing a
   * URL from a ship name is exactly the invention this feature refuses.
   */
  linkKnown = true,
}: {
  tripId: string;
  linkKnown?: boolean;
}) {
  const queryClient = useQueryClient();
  const [outcome, setOutcome] = useState<CruiseImportOutcome | null>(null);
  const [url, setUrl] = useState("");
  const needsLink = !linkKnown;
  const canSubmit = !needsLink || url.trim().length > 0;

  const importMutation = useMutation({
    mutationFn: () => itineraryApi.importCruise(tripId, url.trim() || undefined),
    onSuccess: (result) => {
      setOutcome(result);
      if (result.ok) {
        // The day spine, the completeness read and the trip plan all change
        // when a sailing gains its ports.
        queryClient.invalidateQueries({ queryKey: ["itinerary", tripId] });
        queryClient.invalidateQueries({ queryKey: ["trip-plan", tripId] });
        queryClient.invalidateQueries({ queryKey: ["completeness", tripId] });
      }
    },
    onError: () =>
      setOutcome({
        ok: false,
        reason: "The sailing could not be looked up just now. Nothing was changed.",
      }),
  });

  return (
    <div className="rounded-xl border border-dashed border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/[0.04] px-4 py-3 print:hidden">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))]/[0.10] text-[hsl(var(--accent-foreground))]">
          <Ship className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">Bring in the cruise days</p>
          <p className="text-xs text-muted-foreground">
            {needsLink
              ? "Paste the sailing's page from the cruise line and we'll read the ports and sea days off it. We don't have the link — it was never saved with the trip."
              : "Reads the ports and sea days straight from the cruise line’s own itinerary. Nothing is saved unless the page confirms it."}
          </p>
        </div>
        <Button
          size="sm"
          className="shrink-0"
          disabled={importMutation.isPending || !canSubmit}
          onClick={() => importMutation.mutate()}
        >
          {importMutation.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
              Reading the sailing…
            </>
          ) : (
            "Import cruise itinerary"
          )}
        </Button>
      </div>

      {needsLink && (
        <label className="mt-2 block">
          <span className="sr-only">Cruise sailing link</span>
          <input
            type="url"
            inputMode="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.royalcaribbean.com/itinerary/..."
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40"
          />
        </label>
      )}

      {outcome && !outcome.ok && (
        <div
          role="status"
          className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50/60 px-2 py-1.5 text-xs text-amber-900"
        >
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {/* The reason is shown exactly as the server wrote it. We do not
              soften it into "try again" — the traveller needs to know the
              itinerary is unconfirmed, not that a button misfired. */}
          <span>{outcome.reason}</span>
        </div>
      )}

      {outcome && outcome.ok && (
        <div
          role="status"
          className="mt-2 rounded-md border border-emerald-200 bg-emerald-50/60 px-2 py-1.5 text-xs text-emerald-900"
        >
          Added {outcome.daysWritten} days
          {outcome.ship ? " aboard " + outcome.ship : ""}
          {outcome.sailDate && outcome.disembarkDate
            ? `, ${outcome.sailDate} to ${outcome.disembarkDate}`
            : ""}
          .
          {outcome.ports && outcome.ports.length > 0 && (
            <> Ports: {outcome.ports.join(" · ")}.</>
          )}
        </div>
      )}
    </div>
  );
}
